/**
 * Keeping password managers off the unlock-PIN fields.
 *
 * Chrome, Safari/iCloud Passwords, 1Password and the rest all detect a fill
 * target by `input[type=password]`, and Chrome has deliberately ignored
 * `autocomplete="off"` on password fields for years — so the only reliable way
 * to keep a manager from offering to fill or generate into a 6-digit PIN is
 * not to be a password field at all.
 *
 * The masking then has to come from CSS. That is a real trade-off: on a
 * browser without `-webkit-text-security` the digits would render in plain
 * text, at a counter, which is precisely the shoulder-surfing the PIN exists
 * to limit. So this feature-detects and falls back to type="password" rather
 * than assuming. On the fallback path the manager offers autofill again —
 * annoying, not dangerous, and strictly better than a visible PIN.
 *
 * `-webkit-text-security` is supported by Chrome, Edge, Safari and Opera; the
 * fallback is what Firefox and anything older gets.
 */

let cachedSupport = null;

/**
 * @returns {boolean} true when CSS can mask a plain text input
 */
export function supportsCssTextSecurity() {
  if (cachedSupport !== null) return cachedSupport;

  cachedSupport =
    typeof window !== "undefined" &&
    typeof window.CSS !== "undefined" &&
    typeof window.CSS.supports === "function" &&
    window.CSS.supports("-webkit-text-security", "disc");

  return cachedSupport;
}

/**
 * The input type a PIN field should use on this browser.
 *
 * @returns {"text"|"password"}
 */
export function pinInputType() {
  return supportsCssTextSecurity() ? "text" : "password";
}

/**
 * Attributes the third-party managers honour. Chrome and iCloud Passwords
 * ignore all of these, which is why the type swap above does the real work —
 * these only cover 1Password, LastPass and Bitwarden.
 *
 * @returns {object} v-bind-able attribute map
 */
export function pinIgnoreAttrs() {
  return {
    autocomplete: "off",
    autocorrect: "off",
    autocapitalize: "off",
    spellcheck: "false",
    "data-lpignore": "true",
    "data-1p-ignore": "true",
    "data-bwignore": "true",
    "data-form-type": "other",
  };
}
